/**
 * @file This file contains a limiter.
 */
import * as deque from '@codegrade/deque';
/**
 * Limit the amount of times a function can run concurrently.
 *
 * @param fun The function to be limited.
 * @param opts Options for the limiter.
 * @returns A function equivalent to `fun` except in that its effects may be
 * delayed when called until a spot is free.
 */
export function limit(fun, opts) {
    const waiting = new deque.Deque();
    let running = 0;
    /**
     * Call the next waiting promise if it exists.
     */
    function callNext() {
        const next = waiting.shift();
        if (next != null) {
            next();
        }
    }
    /**
     * Call `fun` and schedule a next running task if it is done.
     *
     * @param args The arguments to pass to `fun`.
     * @returns The return value of `fun`.
     */
    async function call(args) {
        running += 1;
        try {
            return await fun(...args);
        }
        finally {
            running -= 1;
            callNext();
        }
    }
    return (...args) => {
        if (running >= opts.maxConcurrency) {
            return new Promise((resolve, reject) => {
                waiting.push(() => {
                    call(args).then(resolve, reject);
                });
            });
        }
        return call(args);
    };
}
